import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SharedFileProvider } from './contexts/SharedFileContext';
import ShareIntentListener from './components/ShareIntentListener';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import Notifications from './pages/Notifications';
import Projects from './pages/Projects';
import Providers from './pages/Providers';
import Users from './pages/Users';
import CostCenters from './pages/CostCenters';
import FinancialLaunches from './pages/FinancialLaunches';
import Settings from './pages/Settings';
import Layout from './components/Layout';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

// Dashboard router - shows the right dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'provider') return <ProviderDashboard />;
  return <AdminDashboard />;
};

function App() {
  return (
    <AuthProvider>
      <SharedFileProvider>
        <BrowserRouter>
          <ShareIntentListener />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/admin" replace />} />
                    <Route path="/client" element={<Navigate to="/admin" replace />} />
                    <Route
                      path="/admin/*"
                      element={
                        <PrivateRoute allowedRoles={['administrator', 'client', 'provider']}>
                          <Routes>
                            <Route path="/" element={<DashboardRouter />} />
                            <Route path="/projects" element={<Projects />} />
                            <Route path="/providers" element={<Providers />} />
                            <Route path="/users" element={<Users />} />
                            <Route path="/cost-centers" element={<CostCenters />} />
                            <Route path="/financial-launches" element={<FinancialLaunches />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/settings" element={<Settings />} />
                          </Routes>
                        </PrivateRoute>
                      }
                    />
                  </Routes>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
        </BrowserRouter>
      </SharedFileProvider>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
